import React from 'react';
import type { SupplyPosition, DemandPosition, HedgePosition, DerivedHedgePosition } from '../../../sp-types/positions';
import { sumByFormula, sumByFormulaAndMonth, filterByFormulaAndMonth, getQuantity } from '../../../utils/positionUtils';
import { type TimeGranularity, aggregateToBoundaryPeriods, aggregateToSingleBucket, resolveBoundaryMonths, getBoundaryPeriods } from '../../../utils/timeGranularity';

/** Sum all supply POs (purchase_order + ratio_po) for a formula in a given month. */
function sumSupplyPOsForMonth(
  supplyPositions: SupplyPosition[], formulaCode: string, year: number, month: number,
): number {
  return sumByFormulaAndMonth(supplyPositions, formulaCode, year, month, { subtype: 'purchase_order' })
       + sumByFormulaAndMonth(supplyPositions, formulaCode, year, month, { subtype: 'ratio_po' });
}

export const SkeletonList: React.FC = () => (
  <div className="flex flex-col flex-1 gap-3 ">
    <div className=" bg-white flex shadow-md basis-1/5 rounded animate-pulse opacity-80 "></div>
    <div className=" bg-white flex shadow-md basis-1/5 rounded animate-pulse opacity-80"></div>
    <div className=" bg-white flex shadow-md basis-1/5 rounded animate-pulse opacity-80"></div>
    <div className=" bg-white flex flex-1 shadow-md basis-4/5 rounded animate-pulse opacity-80"></div>
  </div>
);

export interface BufferMonthData {
  month: string;
  storage: number;
  po: number;
  ratioPO?: number; // ratio_po quantity (subset of po, no fixed price)
  expectedDemand: number;
  buffer: number;
  absoluteMonth?: number; // For terminals: which month position this terminal represents (0-23)
  //remainingStock: number;
}

export interface BufferData {
  month: string;
  storage: number;
  po: number;
  expectedDemand: number;
  buffer: number;
  remainingStock?: number;
}

export type { TimeGranularity };
export { aggregateToBoundaryPeriods };

export interface HedgePriceData {
  material: string;
  storage: { quantity: number; price: number };
  pos: { quantity: number; price: number };
  financial: { quantity: number; price: number };
  weightedAvgPrice: number;
  totalQuantity: number;
}

/** Converts an amount in a foreign currency to base currency.
 *  Mirrors the signature of useFx().convertToBase but as a plain function type
 *  so pure calculation helpers stay free of React hooks. */
export type FxConverter = (amount: number, currency: string) => number;

/** Identity converter — returns the amount unchanged. Used as fallback. */
export const identityFxConverter: FxConverter = (amount) => amount;

// Calculate hedge price data for a material
export const calculateHedgePriceData = (
  formulaCode: string,
  supplyPositions: SupplyPosition[],
  hedgePositions: HedgePosition[],
  materialsMap: Record<string, any>,
  convertToBase: FxConverter = identityFxConverter,
): HedgePriceData => {
  const material = materialsMap[formulaCode];
  const spotPriceUSD = material?.currentPrice || 0;
  
  // Calculate storage (on-hand inventory) — use actual purchase cost
  const onHandPositions = supplyPositions.filter(
    p => p.formula_code === formulaCode && p.supply_type === 'on_hand_inventory' && !p.exclude_from_calcs,
  );
  let storageQty = 0;
  let storageValue = 0;
  onHandPositions.forEach(p => {
    const qty = Number(p.quantity) || 0;
    const price = Number(p.unit_price_amount) || 0;
    const currency = p.unit_price_currency || 'USD';
    storageQty += qty;
    storageValue += convertToBase(qty * price, currency);
  });
  const storagePriceUSD = storageQty > 0 ? storageValue / storageQty : 0;
  
  // Calculate POs (purchase orders)
  const poPositions = supplyPositions.filter(
    p => p.formula_code === formulaCode && p.is_purchase_order && !p.exclude_from_calcs,
  );
  const poQty = poPositions.reduce((sum, p) => sum + (Number(p.quantity) || 0), 0);
  
  // Calculate weighted average PO price in base currency
  let poTotalValue = 0;
  let poTotalQty = 0;
  poPositions.forEach(p => {
    const qty = Number(p.quantity) || 0;
    const price = Number(p.unit_price_amount) || 0;
    const currency = p.unit_price_currency || 'USD';
    poTotalValue += convertToBase(qty * price, currency);
    poTotalQty += qty;
  });
  const poPriceUSD = poTotalQty > 0 ? poTotalValue / poTotalQty : 0;
  
  // Calculate financial hedges using HedgePosition directly
  const financialQty = sumByFormula(hedgePositions, formulaCode);
  const financialPriceUSD = spotPriceUSD;
  
  // Calculate weighted average price
  const totalQty = storageQty + poQty + Math.abs(financialQty);
  const totalValue = 
    storageValue + 
    poTotalValue + 
    (Math.abs(financialQty) * financialPriceUSD);
  const weightedAvgPrice = totalQty > 0 ? totalValue / totalQty : 0;
  
  return {
    material: material?.name || formulaCode,
    storage: { quantity: storageQty, price: storagePriceUSD },
    pos: { quantity: poQty, price: poPriceUSD },
    financial: { quantity: Math.abs(financialQty), price: financialPriceUSD },
    weightedAvgPrice,
    totalQuantity: totalQty,
  };
};

// ─── Price Insight ──────────────────────────────────────────
// Aggregated view: one bucket for the full horizon, or one per terminal.
// NOT a monthly time series — the added value is the "big picture" summary.

export interface PriceInsightBucket {
  label: string;
  // Quantity breakdown
  onHandQty: number;
  supplyQty: number;
  hedgeQty: number;
  demandQty: number;
  totalSecured: number;       // onHand + supply + hedges
  // Value / price
  onHandValue: number;
  supplyValue: number;
  hedgeValue: number;
  totalValue: number;
  weightedAvgCost: number;    // totalValue / totalSecured
  spotPrice: number;
  priceAdvantage: number;     // % cheaper (positive) or more expensive (negative) vs spot
  // Derived
  coverageRatio: number;      // totalSecured / demandQty
}

// Shared helper: gather per-month quantities & values for a date range
function gatherMonthlyData(
  formulaCode: string,
  supplyPositions: SupplyPosition[],
  demandPositions: DemandPosition[],
  hedgePositions: HedgePosition[],
  spotPrice: number,
  startMonth: Date,
  monthCount: number,
  convertToBase: FxConverter,
) {
  let supplyQty = 0, supplyValue = 0;
  let hedgeQty = 0, hedgeValue = 0;
  let demandQty = 0;

  for (let i = 0; i < monthCount; i++) {
    const d = new Date(startMonth.getFullYear(), startMonth.getMonth() + i, 1);
    const year = d.getFullYear();
    const month1 = d.getMonth() + 1;

    // Supply POs
    const monthPOs = filterByFormulaAndMonth(
      supplyPositions, formulaCode, year, month1, { subtype: 'purchase_order' },
    ) as SupplyPosition[];
    monthPOs.forEach(p => {
      const qty = Number(p.quantity) || 0;
      const price = Number(p.unit_price_amount) || 0;
      const currency = p.unit_price_currency || 'USD';
      supplyQty += qty;
      supplyValue += convertToBase(qty * price, currency);
    });

    // Hedges — use getQuantity() which handles hedge_effect ?? hedged_quantity_mt
    const monthHedges = filterByFormulaAndMonth(
      hedgePositions, formulaCode, year, month1,
    ) as (HedgePosition | DerivedHedgePosition)[];
    monthHedges.forEach(p => {
      const qty = Math.abs(getQuantity(p));
      // For derived hedges, value is conserved: use original_quantity × underlying_price
      const derived = p as DerivedHedgePosition;
      let posValue: number;
      if (derived.derived_from_base && derived.original_quantity && Number(p.underlying_price)) {
        // Value = allocated base qty × base strike price (value conservation)
        posValue = Math.abs(derived.original_quantity) * Number(p.underlying_price);
      } else {
        const price = Number(p.formula_price) || Number(p.underlying_price) || spotPrice;
        posValue = qty * price;
      }
      hedgeQty += qty;
      hedgeValue += posValue;
    });

    // Demand
    demandQty += Math.abs(sumByFormulaAndMonth(demandPositions, formulaCode, year, month1));
  }

  return { supplyQty, supplyValue, hedgeQty, hedgeValue, demandQty };
}

function makeBucket(
  label: string,
  onHandQty: number,
  onHandValue: number,
  gathered: ReturnType<typeof gatherMonthlyData>,
  spotPrice: number,
): PriceInsightBucket {
  const totalSecured = onHandQty + gathered.supplyQty + gathered.hedgeQty;
  const totalValue = onHandValue + gathered.supplyValue + gathered.hedgeValue;
  const weightedAvgCost = totalSecured > 0 ? totalValue / totalSecured : 0;
  const priceAdvantage = spotPrice > 0
    ? ((spotPrice - weightedAvgCost) / spotPrice) * 100
    : 0;
  const coverageRatio = gathered.demandQty > 0 ? totalSecured / gathered.demandQty : 0;

  return {
    label,
    onHandQty,
    supplyQty: gathered.supplyQty,
    hedgeQty: gathered.hedgeQty,
    demandQty: gathered.demandQty,
    totalSecured,
    onHandValue,
    supplyValue: gathered.supplyValue,
    hedgeValue: gathered.hedgeValue,
    totalValue,
    weightedAvgCost,
    spotPrice,
    priceAdvantage,
    coverageRatio,
  };
}

export const calculatePriceInsightData = (
  formulaCode: string,
  supplyPositions: SupplyPosition[],
  demandPositions: DemandPosition[],
  hedgePositions: HedgePosition[],
  spotPrice: number,
  timeGranularity: TimeGranularity = 'Month',
  terminals?: number[],
  horizonMonths: number = 12,
  convertToBase: FxConverter = identityFxConverter,
): PriceInsightBucket[] => {
  const now = new Date();
  const startMonth = new Date(now.getFullYear(), now.getMonth(), 1);

  // On-hand stock — use actual purchase cost from positions, converted to base currency
  const onHandPositions = supplyPositions.filter(
    p => p.formula_code === formulaCode && p.supply_type === 'on_hand_inventory' && !p.exclude_from_calcs,
  );
  let onHandQty = 0;
  let onHandValue = 0;
  onHandPositions.forEach(p => {
    const qty = Number(p.quantity) || 0;
    const price = Number(p.unit_price_amount) || 0;
    const currency = p.unit_price_currency || 'USD';
    onHandQty += qty;
    onHandValue += convertToBase(qty * price, currency);
  });

  console.log('[PriceInsight]', {
    formulaCode, spotPrice, timeGranularity, horizonMonths,
    hedgeCount: hedgePositions.length,
    hedgesForFormula: hedgePositions.filter(h => h.formula_code === formulaCode).length,
    onHandQty,
    onHandValue,
  });

  // Resolve boundary months from granularity
  const boundaryMonths = resolveBoundaryMonths(timeGranularity, terminals, horizonMonths);
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  // Full mode: all data in a single bucket (no horizon limit)
  if (timeGranularity === 'Full') {
    // Use a large horizon (120 months / 10 years) to capture all future position data
    const fullHorizon = 120;
    const gathered = gatherMonthlyData(
      formulaCode, supplyPositions, demandPositions, hedgePositions,
      spotPrice, new Date(now.getFullYear(), now.getMonth(), 1), fullHorizon, convertToBase,
    );
    return [makeBucket('Full', onHandQty, onHandValue, gathered, spotPrice)];
  }

  if (!boundaryMonths) {
    // Month mode: one bucket per month
    const result: PriceInsightBucket[] = [];
    for (let i = 0; i < horizonMonths; i++) {
      const monthStart = new Date(now.getFullYear(), now.getMonth() + i, 1);
      const gathered = gatherMonthlyData(
        formulaCode, supplyPositions, demandPositions, hedgePositions,
        spotPrice, monthStart, 1, convertToBase,
      );
      const label = monthNames[monthStart.getMonth()] + String(monthStart.getFullYear()).substring(2);
      // On-hand only goes into the first bucket
      const bucketOnHand = i === 0 ? onHandQty : 0;
      const bucketOnHandValue = i === 0 ? onHandValue : 0;
      result.push(makeBucket(label, bucketOnHand, bucketOnHandValue, gathered, spotPrice));
    }
    return result;
  }

  // Bucketed mode: one bucket per boundary period (Terminal / Quarter / Year)
  const periods = getBoundaryPeriods(boundaryMonths, horizonMonths);
  const result: PriceInsightBucket[] = [];

  for (let pi = 0; pi < periods.length; pi++) {
    const period = periods[pi];
    const startAbs = pi === 0 ? 0 : periods[pi - 1].absoluteMonth + 1;
    const endAbs = period.absoluteMonth;
    const monthCount = endAbs - startAbs + 1;

    const bucketStart = new Date(now.getFullYear(), now.getMonth() + startAbs, 1);
    const gathered = gatherMonthlyData(
      formulaCode, supplyPositions, demandPositions, hedgePositions,
      spotPrice, bucketStart, monthCount, convertToBase,
    );

    // On-hand only goes into the first bucket
    const bucketOnHand = pi === 0 ? onHandQty : 0;
    const bucketOnHandValue = pi === 0 ? onHandValue : 0;

    result.push(makeBucket(period.label, bucketOnHand, bucketOnHandValue, gathered, spotPrice));
  }

  return result;
};

// Buffer calculation method
export const calculateBufferData = (
  formulaCode: string,
  supplyPositions: SupplyPosition[],
  demandPositions: DemandPosition[],
  bufferAmount: number | { baseAmount: number; lookaheadMonths: number; safetyPercentage: number },
  timeGranularity: TimeGranularity = 'Month',
  terminals?: number[],
): BufferMonthData[] => {
  console.log(`[BufferCalc] Mode: ${timeGranularity}${terminals?.length ? ' (terminals: ' + terminals.join(',') + ')' : ''}`);
  
  const monthsToCalculate = 24;
  const quarterEndMonths = [2, 5, 8, 11]; // March, June, September, December (0-indexed)

  // Parse buffer configuration
  const isLegacyBuffer = typeof bufferAmount === 'number';
  const bufferConfig = isLegacyBuffer 
    ? { baseAmount: bufferAmount, lookaheadMonths: 0, safetyPercentage: 0 }
    : bufferAmount;
  
  console.log('[BufferCalc] Buffer config:', bufferConfig);

  const now = new Date();

  // Get on-hand storage using positionUtils
  let currentStock = sumByFormula(supplyPositions, formulaCode, { subtype: 'on_hand_inventory' });
  console.log('[BufferCalc] On-hand stock:', { formulaCode, currentStock });
  
  const result: BufferMonthData[] = [];

  // Accumulation variables for buffer and PO only (NOT future orders)
  let accumulatedBuffer = 0;
  let accumulatedPO = 0;
  let accumulatedRatioPO = 0;
  let accumulatedFutureOrders = 0;

  for (let i = 0; i < monthsToCalculate; i++) {
    const monthDate = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const currYear = monthDate.getFullYear();
    const currMonth = monthDate.getMonth();
    const month1 = currMonth + 1; // 1-indexed for positionUtils

    // POs for this month (purchase_order + ratio_po)
    const monthPOQuantity = sumSupplyPOsForMonth(supplyPositions, formulaCode, currYear, month1);
    const monthRatioPO = sumByFormulaAndMonth(supplyPositions, formulaCode, currYear, month1, { subtype: 'ratio_po' });

    // Demand for this month
    const monthExpectedDemand = Math.abs(sumByFormulaAndMonth(demandPositions, formulaCode, currYear, month1));

    // Calculate dynamic buffer for this month: Buffer(n) = A + Σ(Demand(n+1 to n+B)) × C%
    let monthBuffer = bufferConfig.baseAmount;
    
    if (bufferConfig.lookaheadMonths > 0 && bufferConfig.safetyPercentage > 0) {
      let lookaheadDemand = 0;
      for (let j = 1; j <= bufferConfig.lookaheadMonths; j++) {
        const futureDate = new Date(currYear, currMonth + j, 1);
        const futureMonth1 = futureDate.getMonth() + 1;
        const futureYear = futureDate.getFullYear();
        lookaheadDemand += Math.abs(sumByFormulaAndMonth(demandPositions, formulaCode, futureYear, futureMonth1));
      }
      monthBuffer += lookaheadDemand * (bufferConfig.safetyPercentage / 100);
    }

    // Accumulate buffer and PO only
    accumulatedBuffer += monthBuffer;
    accumulatedPO += monthPOQuantity;
    accumulatedRatioPO += monthRatioPO;
    accumulatedFutureOrders += monthExpectedDemand;
    // For any non-Month granularity, include ALL months — aggregateToBoundaryPeriods needs them
    const shouldAddDataPoint = true;

    if (shouldAddDataPoint) {
      // Format month key as 'MMMYY' (e.g., 'Jan24')
      const monthKey = monthDate
        .toLocaleDateString('en-US', { month: 'short', year: '2-digit' })
        .replace(' ', '')
        .replace("'", '');

      // Storage for this month is the STARTING storage (before this month's transactions)
      result.push({
        month: monthKey,
        storage: currentStock, // Starting storage for this month
        buffer: accumulatedBuffer,
        po: accumulatedPO,
        ratioPO: accumulatedRatioPO,
        expectedDemand: accumulatedFutureOrders,
      });

      // Calculate next month's starting storage: current + this month's supply - this month's demand
      currentStock = currentStock + accumulatedPO - accumulatedFutureOrders;

      // Reset accumulators for next period
      accumulatedBuffer = 0;
      accumulatedPO = 0;
      accumulatedRatioPO = 0;
      accumulatedFutureOrders = 0;
    } else {
      // Even if we don't add a data point, we still need to update the running stock
      currentStock = currentStock + monthPOQuantity - monthExpectedDemand;
    }
  }

  // Aggregate into coarser buckets if granularity is not Month
  const bufferAggregateBucket = (items: BufferMonthData[], label: string, absoluteMonth: number, runningStorage: number) => {
    let totalPO = 0;
    let totalDemand = 0;
    let terminalBuffer = 0;
    items.forEach((m, i) => {
      totalPO += m.po;
      totalDemand += m.expectedDemand;
      if (i === items.length - 1) terminalBuffer = m.buffer;
    });
    const finalStorage = runningStorage + totalPO - totalDemand;
    return {
      row: {
        month: label,
        storage: runningStorage,
        po: totalPO,
        expectedDemand: totalDemand,
        buffer: terminalBuffer,
        absoluteMonth,
      } as BufferMonthData,
      nextState: finalStorage,
    };
  };

  if (timeGranularity === 'Full') {
    return aggregateToSingleBucket<BufferMonthData, number>({
      monthlyData: result,
      initialState: result[0]?.storage || 0,
      aggregateBucket: bufferAggregateBucket,
    });
  }

  const boundaryMonths = resolveBoundaryMonths(timeGranularity, terminals, monthsToCalculate);
  if (boundaryMonths) {
    return aggregateToBoundaryPeriods<BufferMonthData, number>({
      monthlyData: result,
      boundaryMonths,
      horizonMonths: monthsToCalculate,
      initialState: result[0]?.storage || 0,
      debugKey: '__bufferTerminalDebugLog',
      aggregateBucket: bufferAggregateBucket,
    });
  }

  return result;
};

// Required stock percentages for 12 months (repeating pattern for 24 months)
export const REQUIRED_STOCK_PERCENTAGES = [
  100, 100, 100, 75, 75, 75, 50, 50, 50, 25, 25, 25, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
];

export interface CoverageMonthData {
  month: string;
  stock: number; // Initial storage
  POs: number; // Purchase orders
  financialPositions: number; // Hedges
  expectedDemand: number; // Future orders (demand)
  emergencyStock: number; // Buffer
  currCover: number; // Current coverage: stock + POs + hedges
  reqCover: number; // Required coverage: (demand + buffer) × ratio
  covRatio: number; // Coverage ratio percentage
  covPercentage: number; // Coverage percentage: currCover / (demand + buffer) × 100
  inventory: number; // Total inventory including hedges
  visExpectDemand: number; // Visible demand bar: (demand + buffer) - inventory
  remain: number; // Remaining stock after demand
  physicalCoverage: number; // Physical coverage: stock + POs (for split bar display)
  fullDemand: number; // Full demand + buffer (for overlapping bar approach)
  absoluteMonth?: number; // For terminals: which month position this terminal represents (0-23)
}

export interface LogisticsMonthData {
  month: string;
  POs: number;
  inventoryRep: number;
  futureOrders: number;
  visFutOrders: number;
  visExpectDemand: number;
  financialPositions: number;
  stock: number;
  stockSurplus: number;
  inventory: number;
  remain: number;
  emergencyStock: number;
  covRatio: number;
  logisticAmnt: number;
  expectedDemand: number;
  requiredQuantity: number;
  reqCover: number;
  currCover: number;
  covPercentage: number;
  absoluteMonth?: number; // For terminals: which month position this terminal represents (0-23)
}

export const calculateLogisticsData = (
  formulaCode: string,
  supplyPositions: SupplyPosition[],
  demandPositions: DemandPosition[],
  hedgePositions: HedgePosition[],
  isCoverage: boolean,
  coverageRatio: number[],
  isCovPercent: boolean,
  logisticsAmount: number[],
  isLogisticsPercent: boolean,
  emergencyStock: number | { baseAmount: number; lookaheadMonths: number; safetyPercentage: number },
  timeGranularity: TimeGranularity = 'Month',
  terminals?: number[],
): LogisticsMonthData[] => {
  // Parse buffer config (support both legacy number and new config object)
  const isLegacyBuffer = typeof emergencyStock === 'number';
  const bufferConfig = isLegacyBuffer
    ? { baseAmount: emergencyStock, lookaheadMonths: 0, safetyPercentage: 0 }
    : emergencyStock;
  const monthsToCalculate = 24;
  const quarterEndMonths = [2, 5, 8, 11]; // March, June, September, December (0-indexed)

  // Extend strategy arrays to 24 months using month 12's value for months 13-24
  const extendedLogisticsAmount = [...logisticsAmount];
  const extendedCoverageRatio = [...coverageRatio];
  const lastLogisticsValue = logisticsAmount[11] || 25;
  const lastCoverageValue = coverageRatio[11] || 100;
  while (extendedLogisticsAmount.length < 24) {
    extendedLogisticsAmount.push(lastLogisticsValue);
  }
  while (extendedCoverageRatio.length < 24) {
    extendedCoverageRatio.push(lastCoverageValue);
  }

  const now = new Date();

  // Get on-hand storage using positionUtils
  let currentStock = sumByFormula(supplyPositions, formulaCode, { subtype: 'on_hand_inventory' });
  console.log('[LogisticsCalc] On-hand stock:', { formulaCode, currentStock });
  
  const result: LogisticsMonthData[] = [];

  // Add on-hand storage as first entry
  const onHandBuffer = bufferConfig.baseAmount;
  const onHandEntry: LogisticsMonthData = {
    month: 'On-Hand',
    POs: 0,
    inventoryRep: currentStock,
    futureOrders: 0,
    visFutOrders: 0,
    stock: currentStock,
    stockSurplus: Math.max(0, currentStock - onHandBuffer),
    inventory: isCoverage ? currentStock : Math.max(0, currentStock - onHandBuffer),
    remain: currentStock,
    expectedDemand: 0,
    visExpectDemand: 0,
    requiredQuantity: 0,
    reqCover: 0,
    covPercentage: 0,
    currCover: currentStock,
    financialPositions: 0,
    emergencyStock: onHandBuffer,
    covRatio: 0,
    logisticAmnt: 0,
  };
  result.push(onHandEntry);

  for (let i = 0; i < monthsToCalculate; i++) {
    const monthDate = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const currYear = monthDate.getFullYear();
    const currMonth = monthDate.getMonth();
    const month1 = currMonth + 1; // 1-indexed for positionUtils

    // POs for this month (purchase_order + ratio_po)
    const monthPOQuantity = sumSupplyPOsForMonth(supplyPositions, formulaCode, currYear, month1);

    // Demand for this month
    const monthFutureOrders = Math.abs(sumByFormulaAndMonth(demandPositions, formulaCode, currYear, month1));

    // Hedges for this month (only used in coverage mode)
    const monthFinancialPositions = isCoverage
      ? sumByFormulaAndMonth(hedgePositions, formulaCode, currYear, month1)
      : 0;

    const monthKey = monthDate
      .toLocaleDateString('en-US', { month: 'short', year: '2-digit' })
      .replace(' ', '')
      .replace("'", '');

    // Calculate dynamic buffer for this month: Buffer(n) = A + Σ(Demand(n+1 to n+B)) × C%
    let monthBuffer = bufferConfig.baseAmount;
    if (bufferConfig.lookaheadMonths > 0 && bufferConfig.safetyPercentage > 0) {
      let lookaheadDemand = 0;
      for (let j = 1; j <= bufferConfig.lookaheadMonths; j++) {
        const futureDate = new Date(currYear, currMonth + j, 1);
        const futureMonth1 = futureDate.getMonth() + 1;
        const futureYear = futureDate.getFullYear();
        lookaheadDemand += Math.abs(sumByFormulaAndMonth(demandPositions, formulaCode, futureYear, futureMonth1));
      }
      monthBuffer += lookaheadDemand * (bufferConfig.safetyPercentage / 100);
    }

    const reqCover = (monthFutureOrders + monthBuffer) * ((extendedCoverageRatio[i] || 1) / 100);
    const currCover = Math.max(0, monthPOQuantity + currentStock + monthFinancialPositions);
    const remain = monthPOQuantity + currentStock - monthFutureOrders;
    const inventory = isCoverage 
      ? monthPOQuantity + currentStock + monthFinancialPositions 
      : Math.max(0, monthPOQuantity + currentStock - monthBuffer);
    const monthRow = {
      month: monthKey,
      POs: monthPOQuantity,
      inventoryRep: monthPOQuantity + currentStock,
      futureOrders: monthFutureOrders,
      visFutOrders: monthFutureOrders - inventory < 0 ? 0 : monthFutureOrders - inventory,
      stock: currentStock,
      stockSurplus: Math.max(0, currentStock - monthBuffer),
      inventory,
      remain,
      expectedDemand: monthFutureOrders,
      visExpectDemand: Math.max(0, monthFutureOrders - inventory),
      requiredQuantity: (monthFutureOrders * extendedLogisticsAmount[i]) / 100,
      reqCover,
      covPercentage: (monthFutureOrders + monthBuffer) > 0 ? Math.round((currCover / (monthFutureOrders + monthBuffer)) * 100) : (currCover > 0 ? 999 : 0),
      currCover,
      financialPositions: monthFinancialPositions,
      emergencyStock: monthBuffer,
      covRatio: extendedCoverageRatio[i],
      logisticAmnt: extendedLogisticsAmount[i],
    };
    currentStock = Math.max(0, remain);
    result.push(monthRow);
  }

  // Aggregate into coarser buckets if granularity is not Month
  const logAggregateBucket = (items: LogisticsMonthData[], label: string, absoluteMonth: number, runningStock: number) => {
    const initialStock = runningStock;
    let totalPOs = 0, totalFutureOrders = 0, totalFinancialPositions = 0;
    let terminalEmergencyStock = bufferConfig.baseAmount;
    let sumCovRatio = 0, sumLogisticAmnt = 0, monthCount = 0;

    items.forEach((m, i) => {
      totalPOs += m.POs;
      totalFutureOrders += m.futureOrders;
      totalFinancialPositions += m.financialPositions;
      sumCovRatio += m.covRatio;
      sumLogisticAmnt += m.logisticAmnt;
      monthCount++;
      if (i === items.length - 1) terminalEmergencyStock = m.emergencyStock;
    });

    const avgCovRatio = monthCount > 0 ? sumCovRatio / monthCount : 0;
    const avgLogisticAmnt = monthCount > 0 ? sumLogisticAmnt / monthCount : 0;
    const reqCover = (totalFutureOrders + terminalEmergencyStock) * (avgCovRatio / 100);
    const currCover = Math.max(0, totalPOs + initialStock + totalFinancialPositions);
    const remain = totalPOs + initialStock - totalFutureOrders;
    const inventory = isCoverage
      ? totalPOs + initialStock + totalFinancialPositions
      : Math.max(0, totalPOs + initialStock - terminalEmergencyStock);

    return {
      row: {
        month: label,
        POs: totalPOs,
        inventoryRep: totalPOs + initialStock,
        futureOrders: totalFutureOrders,
        visFutOrders: Math.max(0, totalFutureOrders - inventory),
        stock: initialStock,
        stockSurplus: Math.max(0, initialStock - terminalEmergencyStock),
        inventory,
        remain,
        expectedDemand: totalFutureOrders,
        visExpectDemand: Math.max(0, totalFutureOrders - inventory),
        requiredQuantity: (totalFutureOrders * avgLogisticAmnt) / 100,
        reqCover,
        covPercentage: (totalFutureOrders + terminalEmergencyStock) > 0 ? Math.round((currCover / (totalFutureOrders + terminalEmergencyStock)) * 100) : 0,
        currCover,
        financialPositions: totalFinancialPositions,
        emergencyStock: terminalEmergencyStock,
        covRatio: avgCovRatio,
        logisticAmnt: avgLogisticAmnt,
        absoluteMonth,
      } as LogisticsMonthData,
      nextState: Math.max(0, remain),
    };
  };

  const onHand = result[0]; // Preserve On-Hand entry

  if (timeGranularity === 'Full') {
    const aggregated = aggregateToSingleBucket<LogisticsMonthData, number>({
      monthlyData: result.slice(1),
      initialState: result[1]?.stock || 0,
      aggregateBucket: logAggregateBucket,
    });
    return [onHand, ...aggregated];
  }

  const logBoundaryMonths = resolveBoundaryMonths(timeGranularity, terminals, monthsToCalculate);
  if (logBoundaryMonths) {
    const aggregated = aggregateToBoundaryPeriods<LogisticsMonthData, number>({
      monthlyData: result.slice(1),
      boundaryMonths: logBoundaryMonths,
      horizonMonths: monthsToCalculate,
      initialState: result[1]?.stock || 0,
      debugKey: '__logisticsTerminalDebugLog',
      aggregateBucket: logAggregateBucket,
    });
    return [onHand, ...aggregated];
  }

  return result;
};

export const calculateCoverageData = (
  formulaCode: string,
  supplyPositions: SupplyPosition[],
  demandPositions: DemandPosition[],
  hedgePositions: HedgePosition[],
  coverageRatio: number[],
  emergencyStock: number | { baseAmount: number; lookaheadMonths: number; safetyPercentage: number },
  timeGranularity: TimeGranularity = 'Month',
  terminals?: number[],
): CoverageMonthData[] => {
  // Parse buffer config (support both legacy number and new config object)
  const isLegacyBuffer = typeof emergencyStock === 'number';
  const bufferConfig = isLegacyBuffer
    ? { baseAmount: emergencyStock, lookaheadMonths: 0, safetyPercentage: 0 }
    : emergencyStock;

  const monthsToCalculate = 24;

  // Extend coverage ratio to 24 months
  const extendedCoverageRatio = [...coverageRatio];
  const lastCoverageValue = coverageRatio[11] || 100;
  while (extendedCoverageRatio.length < 24) {
    extendedCoverageRatio.push(lastCoverageValue);
  }

  const now = new Date();

  // Get on-hand storage using positionUtils
  let currentStock = sumByFormula(supplyPositions, formulaCode, { subtype: 'on_hand_inventory' });
  console.log('[CoverageCalc] On-hand stock:', { formulaCode, currentStock });
  
  const result: CoverageMonthData[] = [];

  // On-hand entry for coverage
  const onHandBuffer = bufferConfig.baseAmount;
  const onHandEntry: CoverageMonthData = {
    month: 'On-Hand',
    stock: currentStock,
    POs: 0,
    financialPositions: 0,
    expectedDemand: 0,
    emergencyStock: onHandBuffer,
    currCover: currentStock,
    reqCover: 0,
    covRatio: 0,
    covPercentage: 0,
    inventory: currentStock,
    visExpectDemand: 0,
    remain: currentStock,
    physicalCoverage: currentStock,
    fullDemand: 0,
  };
  result.push(onHandEntry);

  for (let i = 0; i < monthsToCalculate; i++) {
    const monthDate = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const currYear = monthDate.getFullYear();
    const currMonth = monthDate.getMonth();
    const month1 = currMonth + 1; // 1-indexed for positionUtils

    // POs for this month
    const monthPOQuantity = sumByFormulaAndMonth(supplyPositions, formulaCode, currYear, month1, { subtype: 'purchase_order' });

    // Demand for this month
    const monthFutureOrders = Math.abs(sumByFormulaAndMonth(demandPositions, formulaCode, currYear, month1));

    // Hedges for this month
    const monthFinancialPositions = sumByFormulaAndMonth(hedgePositions, formulaCode, currYear, month1);

    const monthKey = monthDate
      .toLocaleDateString('en-US', { month: 'short', year: '2-digit' })
      .replace(' ', '')
      .replace("'", '');
    
    // Calculate dynamic buffer for this month: Buffer(n) = A + Σ(Demand(n+1 to n+B)) × C%
    let monthBuffer = bufferConfig.baseAmount;
    if (bufferConfig.lookaheadMonths > 0 && bufferConfig.safetyPercentage > 0) {
      let lookaheadDemand = 0;
      for (let j = 1; j <= bufferConfig.lookaheadMonths; j++) {
        const futureDate = new Date(currYear, currMonth + j, 1);
        const futureMonth1 = futureDate.getMonth() + 1;
        const futureYear = futureDate.getFullYear();
        lookaheadDemand += Math.abs(sumByFormulaAndMonth(demandPositions, formulaCode, futureYear, futureMonth1));
      }
      monthBuffer += lookaheadDemand * (bufferConfig.safetyPercentage / 100);
    }

    // Coverage calculations
    const currCover = Math.max(0, monthPOQuantity + currentStock + monthFinancialPositions);
    const reqCover = (monthFutureOrders + monthBuffer) * ((extendedCoverageRatio[i] || 100) / 100);
    const remain = monthPOQuantity + currentStock - monthFutureOrders;
    const inventory = monthPOQuantity + currentStock + monthFinancialPositions;
    
    const demandPlusBuffer = monthFutureOrders + monthBuffer;
    const covPercentage = demandPlusBuffer > 0 ? Math.round((currCover / demandPlusBuffer) * 100) : (currCover > 0 ? 999 : 0);
    
    const monthRow: CoverageMonthData = {
      month: monthKey,
      stock: currentStock,
      POs: monthPOQuantity,
      financialPositions: monthFinancialPositions,
      expectedDemand: monthFutureOrders,
      emergencyStock: monthBuffer,
      currCover,
      reqCover,
      covRatio: extendedCoverageRatio[i],
      covPercentage,
      inventory,
      visExpectDemand: Math.max(0, (monthFutureOrders + monthBuffer) - inventory),
      remain,
      physicalCoverage: currentStock + monthPOQuantity,
      fullDemand: monthFutureOrders + monthBuffer,
    };
    currentStock = Math.max(0, remain);
    result.push(monthRow);
  }

  // Aggregate into coarser buckets if granularity is not Month
  const covAggregateBucket = (items: CoverageMonthData[], label: string, absoluteMonth: number, runningStock: number) => {
    const initialStock = runningStock;
    let totalPOs = 0, totalDemand = 0, totalFinancialPositions = 0;
    let terminalEmergencyStock = bufferConfig.baseAmount;
    let sumCovRatio = 0, monthCount = 0;

    items.forEach((m, i) => {
      totalPOs += m.POs;
      totalDemand += m.expectedDemand;
      totalFinancialPositions += m.financialPositions;
      sumCovRatio += m.covRatio;
      monthCount++;
      if (i === items.length - 1) terminalEmergencyStock = m.emergencyStock;
    });

    const avgCovRatio = monthCount > 0 ? sumCovRatio / monthCount : 0;
    const reqCover = (totalDemand + terminalEmergencyStock) * (avgCovRatio / 100);
    const currCover = Math.max(0, totalPOs + initialStock + totalFinancialPositions);
    const remain = totalPOs + initialStock - totalDemand;
    const inventory = totalPOs + initialStock + totalFinancialPositions;
    const demandPlusBuffer = totalDemand + terminalEmergencyStock;
    const covPercentage = demandPlusBuffer > 0 ? Math.round((currCover / demandPlusBuffer) * 100) : (currCover > 0 ? 999 : 0);

    return {
      row: {
        month: label,
        stock: initialStock,
        POs: totalPOs,
        financialPositions: totalFinancialPositions,
        expectedDemand: totalDemand,
        emergencyStock: terminalEmergencyStock,
        currCover,
        reqCover,
        covRatio: avgCovRatio,
        covPercentage,
        inventory,
        visExpectDemand: Math.max(0, (totalDemand + terminalEmergencyStock) - inventory),
        remain,
        physicalCoverage: initialStock + totalPOs,
        fullDemand: totalDemand + terminalEmergencyStock,
        absoluteMonth,
      } as CoverageMonthData,
      nextState: Math.max(0, remain),
    };
  };

  const covOnHand = result[0]; // Preserve On-Hand entry

  if (timeGranularity === 'Full') {
    const aggregated = aggregateToSingleBucket<CoverageMonthData, number>({
      monthlyData: result.slice(1),
      initialState: result[0]?.stock || 0,
      aggregateBucket: covAggregateBucket,
    });
    return [covOnHand, ...aggregated];
  }

  const covBoundaryMonths = resolveBoundaryMonths(timeGranularity, terminals, monthsToCalculate);
  if (covBoundaryMonths) {
    const aggregated = aggregateToBoundaryPeriods<CoverageMonthData, number>({
      monthlyData: result.slice(1),
      boundaryMonths: covBoundaryMonths,
      horizonMonths: monthsToCalculate,
      initialState: result[0]?.stock || 0,
      debugKey: '__coverageTerminalDebugLog',
      aggregateBucket: covAggregateBucket,
    });
    return [covOnHand, ...aggregated];
  }

  return result;
};

export const shorten = (num: number) =>
  new Intl.NumberFormat('en', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(num);
